import { I18n } from '@calendly/i18n'
import { RadioInput } from '@calendly/ui/components/radio-input'
import { BodyText, TitleText } from '@calendly/ui/components/typography'
import { getEventTypeColorToken } from '@calendly/ui/utils/event-type-colors'
import classNames from 'classnames'
import { forwardRef } from 'react'

import { formatDuration } from 'services/event_types'

import {
  eventOptionCardCss,
  eventOptionCardSelectedCss,
  eventOptionLabelCss,
  eventOptionMainCss,
  eventOptionTextCss,
  eventTypeAccentBarCss,
  eventTypeAccentBarFallbackCss,
} from './styles'

const locationLabel = (locationType, locationTypesCount) => {
  if (!locationTypesCount) return null
  const map = {
    physical: I18n.t('publisher.event_types.location.physical.label'),
    custom: I18n.t('publisher.event_types.location.custom.label'),
    inbound_call: I18n.t('publisher.event_types.location.inbound_call.label'),
    outbound_call: I18n.t('publisher.event_types.location.outbound_call.label'),
    zoom_conference: I18n.t('publisher.event_types.location.zoom_conference.label'),
    google_conference: I18n.t('publisher.event_types.location.google_conference.label'),
    gotomeeting_conference: I18n.t('publisher.event_types.location.gotomeeting_conference.label'),
    microsoft_teams_conference: I18n.t('publisher.event_types.location.microsoft_teams_conference.label'),
    webex_conference: I18n.t('publisher.event_types.location.webex_conference.label'),
    ask_invitee: I18n.t('publisher.event_types.location.ask_invitee.label'),
  }
  if (locationTypesCount > 1) {
    return I18n.t('publisher.event_types.card.locations_count', { count: locationTypesCount })
  }
  return map[locationType] ?? null
}

const buildEventTypeMetadataLine = (et) => {
  const durationPart = et.duration_minutes != null
    ? formatDuration(et.duration_minutes, et.duration_options, true)
    : null
  const locationPart = locationLabel(et.location_type, et.location_types_count)
  const kindPart = et.kind_description ?? null
  return [durationPart, locationPart, kindPart].filter(Boolean).join(' • ')
}

const EventTypeAccentBar = forwardRef(({ accentColor, className, ...props }, ref) => {
  const hasAccentColor = Boolean(accentColor)
  const accentBackgroundColor = getEventTypeColorToken(accentColor)
  const style = { backgroundColor: accentBackgroundColor }

  return (
    <div
      ref={ref}
      {...props}
      className={classNames(
        eventTypeAccentBarCss,
        !hasAccentColor && eventTypeAccentBarFallbackCss,
        className
      )}
      style={style}
      aria-hidden
    />
  )
})

export const EventTypeOption = ({
  et,
  checked,
  onSelect,
  radioName,
}) => {
  const meta = buildEventTypeMetadataLine(et)
  const id = `event-type-selection-${et.uuid}`

  return (
    <label htmlFor={id} className={eventOptionLabelCss}>
      <div
        className={classNames(
          eventOptionCardCss,
          checked && eventOptionCardSelectedCss
        )}
      >
        <EventTypeAccentBar accentColor={et.color} />
        <div className={eventOptionMainCss}>
          <RadioInput
            id={id}
            name={radioName}
            value={et.uuid}
            checked={checked}
            onChange={() => onSelect(et.uuid)}
            aria-label={et.name}
          />
          <div className={eventOptionTextCss}>
            <TitleText weight="bold" size="x-small">{et.name}</TitleText>
            {meta ? (
              <BodyText size="small" color="muted">
                {meta}
              </BodyText>
            ) : null}
          </div>
        </div>
      </div>
    </label>
  )
}
